import {
  Container,
  Header,
  Table,
  StatusIndicator,
  Box,
  SpaceBetween,
  Alert,
} from '@cloudscape-design/components';
import React, { useState } from 'react';
import {
  AuditStatus,
  DEFAULT_LANGUAGE,
  QUESTION_TYPE_LABELS,
  QuestionResultType,
  ScoreFilterType,
} from 'src/contstant/commonLabels';

import { Audit, QuestionWithResponse } from 'src/types/helios/Audit';
import {
  calculatePercentage,
  getAuditQuestionResponseStatusIcon,
  formatScore,
} from 'src/utils/generalUtils';
import { LanguageDropdown } from '../LanguageDropdown';
import useUserProfile from 'src/hooks/useUserProfile';
import { getQuestionContent } from 'src/helper/questionHelper';

interface QuestionsTableProps {
  audit: Audit; // Audit data for context
  questions: QuestionWithResponse[]; // Questions with their responses
  selectedFilter: string | null; // Current filter selection
  isLoading: boolean; // Loading state
  showLanguageDropdown?: boolean; // Whether to show language dropdown in header
  paginationControls?: React.ReactNode; // Optional pagination controls to display in header
}

/**
 * QuestionsTable - Displays audit questions with responses in a filterable table
 * Shows different columns based on audit completion status
 */
const QuestionsTable = ({
  audit,
  questions,
  selectedFilter,
  isLoading,
  showLanguageDropdown = true,
  paginationControls,
}: QuestionsTableProps) => {
  const [translationError, setTranslationError] = useState('');

  const { getUserSelectedLanguage, getPreferLanguageList, selectUserLanguage } =
    useUserProfile();

  // Table column definitions - conditionally includes score columns for completed audits
  const columnDefinitions = [
    {
      id: 'questionId',
      header: 'Question ID',
      cell: (item: QuestionWithResponse) => item.questionDetails.questionId,
    },
    {
      id: 'questionTitle',
      header: 'Question Title',
      minWidth: 180,
      maxWidth: window.innerWidth / 8.7,
      cell: (item: QuestionWithResponse) =>
        getQuestionContent(item.questionDetails, getUserSelectedLanguage(), (error) =>
          setTranslationError(error),
        ).questionDetails.title,
    },

    {
      id: 'responseResult',
      header: 'Result',
      cell: (item: QuestionWithResponse) => {
        if (audit.status !== AuditStatus.COMPLETED) {
          return '-';
        }
        const result = item.responseDetails.mainQuestionResponse.responseResult;
        return (
          <StatusIndicator type={getAuditQuestionResponseStatusIcon(result)}>
            {
              QUESTION_TYPE_LABELS[
                item?.responseDetails?.mainQuestionResponse
                  .responseResult as keyof typeof QuestionResultType
              ]
            }
          </StatusIndicator>
        );
      },
    },
    {
      id: 'auditScore',
      header: 'Assigned Score',
      cell: (item: QuestionWithResponse) =>
        audit.status === AuditStatus.COMPLETED
          ? formatScore(item.questionDetails.auditScore)
          : '-',
    },
    {
      id: 'rawScore',
      header: 'Received Score',
      cell: (item: QuestionWithResponse) =>
        audit.status === AuditStatus.COMPLETED
          ? formatScore(item.responseDetails.mainQuestionResponse.responseScore)
          : '-',
    },
    {
      id: 'score',
      header: 'Score %',
      cell: (item: QuestionWithResponse) =>
        audit.status === AuditStatus.COMPLETED
          ? calculatePercentage(
              item?.responseDetails?.mainQuestionResponse?.responseScore || 0,
              item?.questionDetails?.auditScore || 0,
            ) + '%'
          : '-',
    },
  ];

  return (
    <Container
      data-testid="questions-table-container"
      header={
        <Header
          data-testid="questions-table-header"
          variant="h3"
          actions={
            <SpaceBetween direction="horizontal" size="m" alignItems="center">
              {paginationControls}
              {showLanguageDropdown && (
                <LanguageDropdown
                  data-testid="questions-language-dropdown"
                  selectedLanguage={getUserSelectedLanguage()}
                  defaultLanguage={DEFAULT_LANGUAGE}
                  filterLanguges={getPreferLanguageList()}
                  onLanguageChange={(value) => {
                    selectUserLanguage(value);
                  }}
                />
              )}
            </SpaceBetween>
          }
        >
          Questions
        </Header>
      }
    >
      {/* Questions table with filtering applied */}
      <SpaceBetween data-testid="questions-table-content" size={'s'}>
        {translationError && (
          <Alert data-testid="translation-error-alert" type="error">
            Error : {translationError}
          </Alert>
        )}
        <Table
          data-testid="questions-table"
          columnDefinitions={columnDefinitions}
          items={questions}
          loading={isLoading}
          variant="borderless"
          empty={
            <Box
              data-testid="empty-questions-box"
              margin={{ vertical: 'xs' }}
              textAlign="center"
              color="inherit"
            >
              <SpaceBetween data-testid="empty-questions-spacer" size="m">
                <StatusIndicator data-testid="no-questions-indicator" type="warning">
                  No Questions.
                </StatusIndicator>
              </SpaceBetween>
            </Box>
          }
        />
      </SpaceBetween>
    </Container>
  );
};

export default QuestionsTable;
